import type { Call } from '@entities/call';
import { ParticipantType } from '@entities/participants';
import type { Participant } from '@entities/participants';
import type { BaseUsecase } from '@usecases/base-usecase';

export type HoldToggleRequest = {
  putOnHold: boolean;
  participantType: ParticipantType;
  callId?: string;
};

export class InvalidHoldToggleRequest extends Error {
  override name = 'InvalidHoldToggleRequest';
  errors: unknown;

  constructor(message: string, errors: unknown) {
    super(message);
    this.errors = errors;
  }
}

export interface HoldToggleCallRepository {
  getCallForHoldToggle(): Promise<Call>;
  updateHoldStatus(id: string, isOnHold: boolean): Promise<void>;
  getCurrentCall(): Promise<Call>;
  fetchTransferCall(): Promise<Call>;
  updateTransferHoldStatus(to: string, isOnHold: boolean): Promise<void>;
}

export interface HoldToggleParticipantRepository {
  selectParticipantByCallIdAndType(
    callId: string,
    type: ParticipantType,
  ): Promise<Participant>;
}

export interface HoldToggleEventPublisher {
  publish(legId: string, putOnHold: boolean): Promise<void>;
}

export interface HoldToggleUsecase
  extends BaseUsecase<HoldToggleRequest, void> {
  run(call: HoldToggleRequest): Promise<void>;
}

export class HoldToggle implements HoldToggleUsecase {
  constructor(
    private callRepository: HoldToggleCallRepository,
    private participantRepository: HoldToggleParticipantRepository,
    private publisher: HoldToggleEventPublisher,
  ) {}

  async run({
    putOnHold,
    participantType,
    callId: reqCallId,
  }: HoldToggleRequest): Promise<void> {
    const callId =
      reqCallId || (await this.callRepository.getCallForHoldToggle()).id;

    try {
      const { legId } =
        await this.participantRepository.selectParticipantByCallIdAndType(
          callId,
          participantType,
        );
      if (participantType === ParticipantType.EXTERNAL) {
        const call = await this.callRepository.getCurrentCall();
        if (call) {
          await this.callRepository.updateTransferHoldStatus(
            call.to,
            putOnHold,
          );
        }
      } else if (participantType === ParticipantType.THIRD_PARTY) {
        const call = await this.callRepository.fetchTransferCall();
        if (call) {
          await this.callRepository.updateTransferHoldStatus(
            call.to,
            putOnHold,
          );
        }
      }

      await this.publisher.publish(legId, putOnHold);
    } catch (error) {
      throw new InvalidHoldToggleRequest(
        `Participants not found for callId: ${callId} & type: ${participantType}`,
        error,
      );
    }
  }
}
